/**
 * Concrete values for the named color palette — the renderer half of it.
 *
 * The engine resolves a color to a NAME (`blue`, `info`, or whatever
 * `colorize()` hashed to) and holds no hex at all. This module is where names
 * become pixels, and it is the ONLY such place: both backends read it, so the
 * live DOM board and the `toSVG` export can never disagree about what `crimson`
 * is. It also means an exported SVG carries a real hex instead of the
 * `var(--au-k-…)` string resvg cannot resolve.
 *
 * Values are **theme-tuned, not the literal CSS colors**: a name fixes the hue
 * intent and the theme renders it, so `red` reads on a dark board instead of
 * vibrating at `#ff0000`. Adding a theme is one more table below.
 *
 * ⚠️ Scene is dependency-free by design (it is the kernel both backends sit on),
 * so it cannot import the engine's `PALETTE_NAMES`. The two lists are kept in
 * lockstep by the parity test in `viewer-core` — the one package that depends on
 * both. Add a name here and there, or that test fails.
 */

export type PaletteTheme = "dark";

/** Tuned for a dark board: mid-lightness, saturated enough to read against
 *  `#0f172a`, muted enough not to glow. */
const DARK: Readonly<Record<string, string>> = {
  // Roles — mirror `--au-accent/-info/-warn/-err/-ok` in arch.ui/tokens.css.
  accent: "#34d399",
  info: "#60a5fa",
  warn: "#facc15",
  error: "#f87171",
  ok: "#34d399",
  muted: "#cbd0d4",

  // Swatches.
  red: "#f87171",
  green: "#4ade80",
  blue: "#60a5fa",
  yellow: "#facc15",
  orange: "#fb923c",
  purple: "#c084fc",
  gray: "#9ca3af",
  grey: "#9ca3af",
  pink: "#f472b6",
  brown: "#b98a6a",
  cyan: "#06b6d4",
  magenta: "#e879f9",
  lime: "#a3e635",
  teal: "#14b8a6",
  navy: "#7c93d8",
  maroon: "#a3646e",
  olive: "#bdbf5a",
  silver: "#d1d5db",
  gold: "#fbbf24",
  indigo: "#818cf8",
  violet: "#a78bfa",
  crimson: "#f43f5e",
  coral: "#fb8a67",
  salmon: "#fca5a5",
  khaki: "#e5d98c",
  turquoise: "#5eead4",
  black: "#0b1220",
  white: "#f8fafc",
};

export const PALETTE_VALUES: Readonly<Record<PaletteTheme, Readonly<Record<string, string>>>> = {
  dark: DARK,
};

/**
 * Resolve a color token to a concrete CSS color.
 *
 * **Total and idempotent**: a palette name becomes the theme's hex; anything
 * else — a literal hex, `rgb()`, `hsl()`, `transparent`, even an unrecognized
 * string — comes back untouched. That is deliberate, so every paint boundary can
 * call it without caring whether the value has already been resolved or where it
 * came from.
 */
export function paletteColor(value: string, theme: PaletteTheme = "dark"): string {
  const t = value.trim().toLowerCase();
  const table = PALETTE_VALUES[theme];
  // `hasOwn`, not a bare lookup: the table is an object literal, so `table[t]`
  // walks Object.prototype and `constructor` / `__proto__` come back as a
  // function / an object. Both then reach a paint attribute, where the declared
  // `string` return is a lie and `hex.replace` throws.
  return Object.hasOwn(table, t) ? table[t]! : value;
}
